// The landing record and its starfield, painted together on canvas.starfield
// (pinned to #accueil) by one frame loop. The DOM keeps only an empty .vinyl
// square, which reserves the record's place and catches drag-to-rotate, and
// the .yy-nav links, which are the menu: hovering or focusing one tints the
// label, the halo and a set of stars in that side's colour.
//
// Why a canvas: as stacked DOM layers (rotating div, blurred shadows, SVG
// filter, blend modes) over an animated backdrop, the record forced a full
// re-composite every frame and lagged in Firefox. Here a frame is a few path
// fills plus one cached drawImage, at ~20 fps when idle (the label edge moves
// < 0.5px per frame) and every frame while dragging or fading a hover.
//
// Drag: press-and-drag on the record rotates it 1:1 around its centre, like
// scratching; auto-rotation resumes from the new angle on release. Stars orbit
// at STAR_RATIO of the record's angle, so they follow a scratch too. Under
// prefers-reduced-motion the scene is painted still and repainted only on
// hover/focus and resize.

const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
const sky = document.querySelector<HTMLCanvasElement>(".starfield");
const ctx = sky?.getContext("2d");
const vinyl = document.querySelector<HTMLElement>(".vinyl");

const REV_MS = 66_000;
const STAR_RATIO = 0.2;
const TAU = Math.PI * 2;
const SHADOW_PAD = 60; // room around the record for its drop shadow
// Star trails: strokes on an offscreen canvas that fades by alpha over time.
// Idle drift leaves barely-there tails; a slow drag a visible tail; a fast
// spin outruns the fade and closes the tails into rings.
const TRAIL_MS = 600; // e-folding time of a trail's alpha
// Fade in 100ms steps, not per frame: 8-bit alpha times a tiny per-frame
// factor rounds back up, leaving faint rings forever; bigger steps leave
// at most ~1% alpha behind.
const FADE_EVERY = 100;
const TRAIL_RES = 0.5; // trails are soft; half resolution, a quarter the pixels
const GOLD = [200, 169, 110];
const AMBER = [235, 172, 80]; // Yin's halo: warmer and stronger than idle gold
const INDIGO = [123, 104, 238];
const BODY = [16, 16, 23]; // the record's black, where a dimmed half sinks
const YIN_INK = [206, 198, 186]; // paper cream at rest, muted so hover can glow
const YIN_LIT = [255, 236, 192]; // yin glowing warm
const YANG_INK = [13, 13, 26];
const YANG_LIT = [26, 24, 51]; // yang warmed toward indigo
// Per-channel blend from colour a toward b; used for every hover colour.
const mix = (a: number[], b: number[], t: number) =>
  a.map((v, i) => v + (b[i]! - v) * t);

type Side = "yin" | "yang";
// Yin = face A (Bain sonore, warm gold), Yang = face B (Electro, indigo).
const SIDE_COLOR: Record<Side, string> = { yin: "#f0d49a", yang: "#9d8cff" };
// Border of the hovered half (and its nav text colour in landing.css):
// saturated so Yin's reads against both its cream and the black record.
const BORDER: Record<Side, string> = {
  yin: "rgb(235, 172, 80)",
  yang: "#9d8cff",
};
const TINT: Record<Side, string> = {
  yin: "rgba(200, 169, 110, 0.85)",
  yang: "rgba(123, 104, 238, 0.9)",
};
const litTarget: Record<Side, number> = { yin: 0, yang: 0 };
const lit: Record<Side, number> = { yin: 0, yang: 0 };

// Mostly cream, some gold, rare indigo. Each side lights its own quarter.
const STAR_COLORS = [
  "#f5ede0",
  "#f5ede0",
  "#f5ede0",
  "#f5ede0",
  "#f5ede0",
  "#c8a96e",
  "#c8a96e",
  "#7b68ee",
];
const stars = Array.from({ length: 80 }, (_, i) => ({
  u: Math.random(), // area-uniform radial position: 0 = record rim, 1 = farthest corner
  a: Math.random() * TAU,
  r: 0,
  size: 0.9 + Math.random() * 0.7,
  color: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)]!,
  alpha: 0.5 + Math.random() * 0.5,
  phase: Math.random() * TAU,
  side: (["yin", "yang", null, null] as const)[i % 4],
}));

// Taijitu halves in a 200×200 box centred on (100,100), built from canvas
// arcs: SVG-string arcs whose radius is exactly half the chord render
// unreliably. Both halves share the S curve (bottom lobe bulging left, top
// lobe bulging right); only the outer edge differs. Yang holds the top lobe.
const half = (outerViaLeft: boolean) => {
  const p = new Path2D();
  p.arc(100, 100, 100, -Math.PI / 2, Math.PI / 2, outerViaLeft);
  p.arc(100, 150, 50, Math.PI / 2, -Math.PI / 2, false);
  p.arc(100, 50, 50, Math.PI / 2, -Math.PI / 2, true);
  p.closePath();
  return p;
};
const YIN = half(false);
const YANG = half(true);

let cx = 0;
let cy = 0;
let r = 0;
let record: HTMLCanvasElement | undefined; // static: shadow, body, grooves, rim
let specular: CanvasGradient | undefined; // static light arc over the turning label
const trail = document.createElement("canvas");
const trailCtx = trail.getContext("2d")!;
let lastTurn = 0;
let faded = -Infinity;

const paintRecord = () => {
  const half = r + SHADOW_PAD;
  const c = document.createElement("canvas");
  c.width = c.height = Math.ceil(half * 2 * devicePixelRatio);
  const g = c.getContext("2d")!;
  // origin at the record centre; translation is in device pixels
  const dpr = devicePixelRatio;
  g.setTransform(dpr, 0, 0, dpr, half * dpr, half * dpr);

  const shadow = g.createRadialGradient(0, 18, r - 40, 0, 18, r + 38);
  shadow.addColorStop(0, "rgba(0, 0, 0, 0.8)");
  shadow.addColorStop(1, "rgba(0, 0, 0, 0)");
  g.fillStyle = shadow;
  g.fillRect(-half, -half, half * 2, half * 2);

  const body = g.createRadialGradient(0, 0, 0, 0, 0, r);
  body.addColorStop(0, "#1a1a22");
  body.addColorStop(0.42, "#101017");
  body.addColorStop(0.72, "#08080d");
  body.addColorStop(1, "#050509");
  g.fillStyle = body;
  g.beginPath();
  g.arc(0, 0, r, 0, TAU);
  g.fill();

  // grooves: dark bands with a faint light line every 2.5px
  g.fillStyle = "rgba(0, 0, 0, 0.3)";
  g.fill();
  g.beginPath();
  for (let rr = 1; rr < r; rr += 2.5) {
    g.moveTo(rr, 0);
    g.arc(0, 0, rr, 0, TAU);
  }
  g.strokeStyle = "rgba(255, 255, 255, 0.022)";
  g.lineWidth = 1;
  g.stroke();

  g.beginPath();
  g.arc(0, 0, r + 0.5, 0, TAU);
  g.strokeStyle = "rgba(0, 0, 0, 0.6)";
  g.stroke();
  record = c;
};

const layout = () => {
  if (!sky || !ctx || !vinyl) return;
  const box = sky.getBoundingClientRect();
  const d = vinyl.getBoundingClientRect();
  sky.width = Math.round(box.width * devicePixelRatio);
  sky.height = Math.round(box.height * devicePixelRatio);
  cx = d.left + d.width / 2 - box.left;
  cy = d.top + d.height / 2 - box.top;
  r = d.width / 2;
  const outer = Math.hypot(
    Math.max(cx, box.width - cx),
    Math.max(cy, box.height - cy),
  );
  for (const s of stars) s.r = Math.sqrt(r * r + s.u * (outer * outer - r * r));
  paintRecord();
  trail.width = Math.round(box.width * devicePixelRatio * TRAIL_RES);
  trail.height = Math.round(box.height * devicePixelRatio * TRAIL_RES);
  // CSS linear-gradient(115deg) across the record's box, fixed in place
  const dx = Math.sin((115 * Math.PI) / 180);
  const dy = -Math.cos((115 * Math.PI) / 180);
  const len = r * (Math.abs(dx) + Math.abs(dy));
  specular = ctx.createLinearGradient(
    cx - dx * len,
    cy - dy * len,
    cx + dx * len,
    cy + dy * len,
  );
  specular.addColorStop(0.3, "rgba(255, 255, 255, 0)");
  specular.addColorStop(0.47, "rgba(255, 255, 255, 0.05)");
  specular.addColorStop(0.5, "rgba(255, 255, 255, 0.09)");
  specular.addColorStop(0.53, "rgba(255, 255, 255, 0.04)");
  specular.addColorStop(0.7, "rgba(255, 255, 255, 0)");
};

const draw = (deg: number, t: number) => {
  if (!sky || !ctx || !record || !specular) return;
  const a = (deg * Math.PI) / 180;
  ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
  ctx.clearRect(0, 0, sky.width, sky.height);

  const turn = a * STAR_RATIO;
  const cssW = sky.width / devicePixelRatio;
  const cssH = sky.height / devicePixelRatio;
  if (!reduce) {
    const ts = devicePixelRatio * TRAIL_RES;
    trailCtx.setTransform(ts, 0, 0, ts, 0, 0);
    if (t - faded >= FADE_EVERY) {
      trailCtx.globalCompositeOperation = "destination-out";
      trailCtx.globalAlpha = 1 - Math.exp(-(t - faded) / TRAIL_MS);
      trailCtx.fillRect(0, 0, cssW, cssH);
      trailCtx.globalCompositeOperation = "source-over";
      faded = t;
    }
    // each star strokes the arc it travelled since the last frame
    const sweep = turn - lastTurn;
    trailCtx.lineCap = "round";
    for (const s of stars) {
      trailCtx.globalAlpha = s.alpha * 0.4;
      trailCtx.strokeStyle = s.color;
      trailCtx.lineWidth = s.size * 1.6;
      trailCtx.beginPath();
      if (Math.abs(sweep) >= TAU) trailCtx.arc(cx, cy, s.r, 0, TAU);
      else trailCtx.arc(cx, cy, s.r, s.a + lastTurn, s.a + turn, sweep < 0);
      trailCtx.stroke();
    }
    lastTurn = turn;
    ctx.drawImage(trail, 0, 0, cssW, cssH);
  }

  // star heads, behind the record
  for (const s of stars) {
    const x = cx + Math.cos(s.a + turn) * s.r;
    const y = cy + Math.sin(s.a + turn) * s.r;
    ctx.globalAlpha = s.alpha * (0.65 + 0.35 * Math.sin(t * 0.0015 + s.phase));
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(x, y, s.size, 0, TAU);
    ctx.fill();
    const glow = s.side ? lit[s.side] : 0;
    if (!s.side || glow < 0.01) continue;
    // lit star: soft halo + bright core, faded in by globalAlpha
    ctx.fillStyle = SIDE_COLOR[s.side];
    ctx.globalAlpha = glow * 0.18;
    ctx.beginPath();
    ctx.arc(x, y, s.size * 5, 0, TAU);
    ctx.fill();
    ctx.globalAlpha = glow;
    ctx.beginPath();
    ctx.arc(x, y, s.size * 1.8, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // halo: gold breathing when idle, warmer (Yin) or indigo (Yang) on hover
  const hover = Math.max(lit.yin, lit.yang);
  const breathe = 0.18 + 0.08 * Math.sin((t / 6000) * TAU);
  const haloAlpha = breathe * (1 - hover) + 0.5 * lit.yin + 0.45 * lit.yang;
  const rgb = mix(mix(GOLD, AMBER, lit.yin), INDIGO, lit.yang).map(Math.round);
  const halo = ctx.createRadialGradient(cx, cy, r * 0.9, cx, cy, r + 80);
  halo.addColorStop(0, `rgba(${rgb}, ${haloAlpha})`);
  halo.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = halo;
  ctx.fillRect(cx - r - 80, cy - r - 80, (r + 80) * 2, (r + 80) * 2);

  const half = r + SHADOW_PAD;
  ctx.drawImage(record, cx - half, cy - half, half * 2, half * 2);

  // turning part: off-centre gloss bloom + taijitu label
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(a);
  const gloss = ctx.createRadialGradient(
    -0.24 * r,
    -0.36 * r,
    0,
    -0.24 * r,
    -0.36 * r,
    0.7 * r,
  );
  gloss.addColorStop(0, "rgba(255, 255, 255, 0.08)");
  gloss.addColorStop(1, "rgba(255, 255, 255, 0)");
  ctx.fillStyle = gloss;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();

  const k = (0.42 * r) / 100; // label is 42% of the record's diameter
  ctx.scale(k, k);
  ctx.translate(-100, -100);
  // Opaque fills only: the halves never show through each other. Hover
  // brightens the chosen side, sinks the other toward the record's black,
  // and traces the chosen half in its side colour. Each eye wears the
  // opposite side's colour, so the hovered side's eye shines in the other.
  const yin = mix(mix(YIN_INK, YIN_LIT, lit.yin), BODY, 0.75 * lit.yang);
  const yang = mix(mix(YANG_INK, YANG_LIT, lit.yang), BODY, 0.75 * lit.yin);
  ctx.fillStyle = `rgb(${yin})`;
  ctx.beginPath();
  ctx.arc(100, 100, 100, 0, TAU);
  ctx.fill();
  ctx.fillStyle = `rgb(${yang})`;
  ctx.fill(YANG);
  ctx.beginPath();
  ctx.arc(100, 150, 7, 0, TAU);
  ctx.fill();
  ctx.fillStyle = `rgb(${yin})`;
  ctx.beginPath();
  ctx.arc(100, 50, 7, 0, TAU);
  ctx.fill();
  // hovered half: wide soft glow, then a thin bright core line
  for (const [side, half] of [
    ["yin", YIN],
    ["yang", YANG],
  ] as const) {
    if (lit[side] < 0.01) continue;
    ctx.strokeStyle = BORDER[side];
    ctx.globalAlpha = lit[side] * 0.3;
    ctx.lineWidth = 8;
    ctx.stroke(half);
    ctx.globalAlpha = lit[side];
    ctx.lineWidth = 2;
    ctx.stroke(half);
  }
  ctx.globalAlpha = 1;
  ctx.strokeStyle = "rgba(200, 169, 110, 0.35)";
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.arc(100, 100, 99, 0, TAU);
  ctx.stroke();
  ctx.restore();

  // centre wash over the label, soft-light, per hovered side
  ctx.globalCompositeOperation = "soft-light";
  for (const side of ["yin", "yang"] as const) {
    if (lit[side] < 0.01) continue;
    const wash = ctx.createRadialGradient(cx, cy, 0, cx, cy, 0.56 * r);
    wash.addColorStop(0, TINT[side]);
    wash.addColorStop(0.7, "rgba(0, 0, 0, 0)");
    ctx.globalAlpha = lit[side];
    ctx.fillStyle = wash;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;

  // fixed light arc: stays put while the record turns
  ctx.fillStyle = specular;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.fill();

  // spindle: bright central star with a pulsing glow (4s)
  const pulse = reduce ? 0 : 0.5 - 0.5 * Math.cos((t / 4000) * TAU);
  ctx.fillStyle = `rgba(245, 237, 224, ${0.35 * (0.6 + 0.4 * pulse)})`;
  ctx.beginPath();
  ctx.arc(cx, cy, 8 * k * (1 + 0.35 * pulse), 0, TAU);
  ctx.fill();
  ctx.fillStyle = "#f5ede0";
  ctx.strokeStyle = "rgba(200, 169, 110, 0.5)";
  ctx.lineWidth = 0.6 * k;
  ctx.beginPath();
  ctx.arc(cx, cy, 3 * k, 0, TAU);
  ctx.fill();
  ctx.stroke();
};

let angle = 0; // record angle in degrees, unwrapped; stars follow at STAR_RATIO

// Each side lights from its menu link (hover, or keyboard focus as
// :focus-visible) or from the pointer over its half of the record.
const sources = {
  yin: { link: false, focus: false, disc: false },
  yang: { link: false, focus: false, disc: false },
};
const links = {
  yin: document.querySelector<HTMLAnchorElement>(".yy-nav-link-yin"),
  yang: document.querySelector<HTMLAnchorElement>(".yy-nav-link-yang"),
};
const relight = (side: Side) => {
  const s = sources[side];
  litTarget[side] = s.link || s.focus || s.disc ? 1 : 0;
  links[side]?.classList.toggle("is-lit", litTarget[side] === 1);
  if (!reduce) return; // the frame loop eases toward the target
  lit[side] = litTarget[side];
  draw(angle, 0);
};
for (const side of ["yin", "yang"] as const) {
  const link = links[side];
  if (!link) continue;
  const set = (key: "link" | "focus", on: boolean) => {
    sources[side][key] = on;
    relight(side);
  };
  link.addEventListener("pointerenter", () => set("link", true));
  link.addEventListener("pointerleave", () => set("link", false));
  link.addEventListener("focus", () =>
    set("focus", link.matches(":focus-visible")),
  );
  link.addEventListener("blur", () => set("focus", false));
}

// Which half of the label is under a client point, or null off the label.
// Undo the record's rotation and scale into the 200-unit label box: Yang
// holds the top lobe and the left half, Yin the bottom lobe and the right.
const halfAt = (clientX: number, clientY: number): Side | null => {
  if (!sky) return null;
  const box = sky.getBoundingClientRect();
  const x = clientX - box.left - cx;
  const y = clientY - box.top - cy;
  const a = (angle * Math.PI) / 180;
  const k = (0.42 * r) / 100;
  const lx = (x * Math.cos(a) + y * Math.sin(a)) / k;
  const ly = (y * Math.cos(a) - x * Math.sin(a)) / k;
  if (lx * lx + ly * ly > 100 * 100) return null;
  if (lx * lx + (ly + 50) ** 2 <= 50 * 50) return "yang";
  if (lx * lx + (ly - 50) ** 2 <= 50 * 50) return "yin";
  return lx < 0 ? "yang" : "yin";
};

// Press-and-drag rotates the record 1:1 around its centre, like scratching
// (not under reduced motion). A press that ends within 6px of where it
// started is a click: it opens the half under it, like the menu link.
let isDragging = false;
let pointer: { x: number; y: number } | null = null; // last hover point over the record
// The record turns under a still cursor, so the frame loop re-runs this too.
const hoverDisc = () => {
  const side = pointer && halfAt(pointer.x, pointer.y);
  for (const s of ["yin", "yang"] as const) {
    if (sources[s].disc === (side === s)) continue;
    sources[s].disc = side === s;
    relight(s);
  }
  if (vinyl) vinyl.style.cursor = side ? "pointer" : "";
};
if (vinyl) {
  let activePointerId = -1;
  let centerX = 0;
  let centerY = 0;
  let prevAngle = 0;
  let downX = 0;
  let downY = 0;

  const angleFromPointer = (clientX: number, clientY: number) =>
    (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI;

  vinyl.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const rect = vinyl.getBoundingClientRect();
    centerX = rect.left + rect.width / 2;
    centerY = rect.top + rect.height / 2;
    prevAngle = angleFromPointer(e.clientX, e.clientY);
    downX = e.clientX;
    downY = e.clientY;
    activePointerId = e.pointerId;
    vinyl.setPointerCapture(e.pointerId);
  });
  vinyl.addEventListener("pointermove", (e) => {
    if (e.pointerId !== activePointerId) {
      pointer = { x: e.clientX, y: e.clientY };
      hoverDisc();
      return;
    }
    if (reduce) return;
    const cur = angleFromPointer(e.clientX, e.clientY);
    let delta = cur - prevAngle;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    angle += delta;
    prevAngle = cur;
    if (delta !== 0) isDragging = true;
  });
  vinyl.addEventListener("pointerup", (e) => {
    if (e.pointerId !== activePointerId) return;
    activePointerId = -1;
    isDragging = false;
    if (Math.hypot(e.clientX - downX, e.clientY - downY) >= 6) return;
    const side = halfAt(e.clientX, e.clientY);
    const href = side && links[side]?.href;
    if (href) location.assign(href);
  });
  vinyl.addEventListener("pointercancel", () => {
    activePointerId = -1;
    isDragging = false;
  });
  vinyl.addEventListener("pointerleave", () => {
    pointer = null;
    hoverDisc();
  });
}

if (vinyl && !reduce) {
  let last = performance.now();
  let drawn = -Infinity;

  const tick = (t: number) => {
    const dt = t - last;
    last = t;
    if (!isDragging) angle += (360 / REV_MS) * dt;
    const fading =
      Math.abs(litTarget.yin - lit.yin) + Math.abs(litTarget.yang - lit.yang) >
      0.002;
    if (isDragging || fading || t - drawn >= 50) {
      if (pointer && !isDragging) hoverDisc();
      // ease hover toward its target, ~0.45s
      const ease = Math.min(1, (t - drawn) / 150);
      lit.yin += (litTarget.yin - lit.yin) * ease;
      lit.yang += (litTarget.yang - lit.yang) * ease;
      drawn = t;
      draw(angle, t);
    }
    requestAnimationFrame(tick);
  };

  let resizeRaf = 0;
  window.addEventListener("resize", () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      layout();
    });
  });
  layout();
  document.fonts.ready.then(layout);
  requestAnimationFrame(tick);
} else {
  const still = () => {
    layout();
    draw(angle, 0);
  };
  window.addEventListener("resize", still);
  document.fonts.ready.then(still);
  still();
}
